import { Rules } from "./Rules";
import { ICharacterHistory } from "../interfaces/ICharacterHistory";
import { Revision } from "../interfaces/Revision";
import { ActionOnKey } from "../interfaces/ActionOnKey";
import { RankAction } from "../constants/constants";
import { CurrentBonuses } from "../interfaces/BonusSourceDetails";
import { ActionDelegate, DefinedAction } from "./RpgActions";
import { RuleModule } from "./RuleModule";
import { CalculateBonuses } from "./CalculateBonuses";
import { AddAlert } from "./AlertCenter";
import { ActionResult } from "../interfaces/ActionResult";
import { AlertLevel } from "../types/AlertTypes";
import { IXPModule } from "../interfaces/IXPModule";
import { XPConfigs } from "../interfaces/IXPSystem";
import { UpdateRankOptions } from "../interfaces/UpdateRankOptions";
import { spentXPDetailsSample } from "../interfaces/SpentXPDetails";

export class CharacterHistory implements ICharacterHistory {
  revisionHistory: Revision[] = [
    {
      rankData: {},
      spentXPDetails: false ? spentXPDetailsSample : {},
      log: "Initial state.",
    },
  ];
  revisionNumber: number = 0;

  constructor(
    externalCallback: () => void,
    rules: Rules,
    addActionsOrReset: any,
    addItemAlert: AddAlert
  ) {
    this.externalCallback = externalCallback;
    this.addItemAlert = addItemAlert;
    this.setActionDelegates(addActionsOrReset, rules);
  }

  public setActionDelegates = (addActionsOrReset: any, rules: Rules) => {
    const actions: DefinedAction[] = [];
    for (let ruleItemName in rules.moduleItemNameToModuleKey) {
      actions.push({
        actionName: `update${ruleItemName}`,
        paramNames: ["Key", "inc | dec"],
        allowOneLess: false,
        actionCallback: (key: string, action: string) => {
          const ruleModule = rules.getRuleModuleByRuleItem(key);
          if (!ruleModule) {
            return {
              success: false,
              message: `Can't update '${key}' when no rules exist for it.`,
              level: AlertLevel._1_ERROR,
            };
          }
          return this.updateRank({
            updateRankAction: { key, action: action as RankAction },
            ruleItemConfig: ruleModule.config,
          });
        },
      });
    }
    const actionDelegate: ActionDelegate = {
      delegateKey: "character",
      newActions: actions,
    };
    addActionsOrReset(actionDelegate);
  };

  public externalCallback = () => {
    alert(
      "CharacterHistory external callback not implemented for this instance!"
    );
  };

  public addItemAlert(key: string, actionResult: ActionResult): ActionResult {
    const message =
      "CharacterHistory alerting not implemented for this instance!";
    alert(message);
    return { message, success: false, level: AlertLevel._0_CRITICAL };
  }

  public getCurrent = () => {
    return this.revisionHistory[this.revisionNumber];
  };

  public getCurrentCopy = (): Revision => {
    return JSON.parse(JSON.stringify(this.getCurrent()));
  };

  public calculateCurrentBonuses = (
    ruleModules: RuleModule[],
    xpModules: IXPModule[]
  ): CurrentBonuses => {
    return new CalculateBonuses(this.getCurrent()).calculateBonuses(
      ruleModules,
      xpModules
    );
  };

  public hasCurrentRank = (key: string): boolean => {
    return key in this.getCurrent().rankData;
  };

  public jumpTo = (revision: number) => {
    this.revisionNumber = revision;
    this.externalCallback();
  };

  public updateRank = (updateRankOptions: UpdateRankOptions): ActionResult => {
    const {
      updateRankAction,
      ruleItemConfig,
      levelUpOptionsAndConfig,
    } = updateRankOptions;
    /**
     * TODO: levelUpOptions from specific RuleDetails.
     *
     * TODO: implement exercising XP.
     * Update ranks of (implicitly) XP items.
     * There will be an array of XP spent choices,
     * and decrementing will keep but not apply the choices.
     */
    const revisionHistory = this.revisionHistory.slice(
      0,
      this.revisionNumber + 1
    );
    /**
     * TODO: use a subset of CalculateBonuses functionality
     * to determine the adjusted rank to see if the rank can be changed
     * within the min and max (if present) or not.
     */
    // Relevant RuleItem/XPItem configs
    const configs: XPConfigs = {
      ...levelUpOptionsAndConfig?.xpConfigs,
      [updateRankAction.key]: ruleItemConfig,
    };
    // Initial action and additional actions that should be done in the same revision
    const updateActions: ActionOnKey[] = [
      updateRankAction,
      ...(levelUpOptionsAndConfig?.levelUpOptions.map((levelUpOption) => {
        return {
          key: levelUpOption.key,
          action: updateRankAction.action,
          value: levelUpOption.value,
        };
      }) || []),
    ];
    const currentStats = this.getCurrentCopy();
    const results: ActionResult[] = updateActions.map(
      (updateAction: ActionOnKey) => {
        if (!this.hasCurrentRank(updateAction.key)) {
          currentStats.rankData[updateAction.key] = {
            rank: configs[updateAction.key].default,
          };
        }
        currentStats.log = updateAction.log
          ? updateAction.log
          : "No message specified.";
        const multiplier = configs[updateAction.key]?.rankMultiplier || 1;
        const value: number = updateAction.value
          ? updateAction.value * multiplier
          : multiplier;
        switch (updateAction.action) {
          case "inc":
            if (
              currentStats.rankData[updateAction.key].rank >=
              configs[updateAction.key].max
            ) {
              return this.addItemAlert(updateAction.key, {
                success: false,
                message: `Can not increase rank past ${
                  configs[updateAction.key].max
                }.`,
                level: AlertLevel._2_RULE,
              });
            }
            if (
              currentStats.rankData[updateAction.key].rank + value >
              configs[updateAction.key].max
            ) {
              return this.addItemAlert(updateAction.key, {
                success: false,
                message: `TODO: can't update past max value`,
                level: AlertLevel._0_CRITICAL,
              });
            }
            currentStats.rankData[updateAction.key].rank =
              currentStats.rankData[updateAction.key].rank + value;
            break;
          case "dec":
            if (
              currentStats.rankData[updateAction.key].rank <=
              configs[updateAction.key].min
            ) {
              return this.addItemAlert(updateAction.key, {
                success: false,
                message: `Can not decrease rank past ${
                  configs[updateAction.key].min
                }.`,
                level: AlertLevel._2_RULE,
              });
            }
            if (
              currentStats.rankData[updateAction.key].rank + value <
              configs[updateAction.key].min
            ) {
              return this.addItemAlert(updateAction.key, {
                success: false,
                message: `TODO: can't update past min value`,
                level: AlertLevel._0_CRITICAL,
              });
            }
            currentStats.rankData[updateAction.key].rank =
              currentStats.rankData[updateAction.key].rank - value;
            if (
              currentStats.rankData[updateAction.key].rank <=
                configs[updateAction.key].min &&
              configs[updateAction.key].selectOptions
            ) {
              delete currentStats.rankData[updateAction.key];
            }
            break;
          case "reset":
            delete currentStats.rankData[updateAction.key];
            break;
          default:
            return this.addItemAlert(updateAction.key, {
              success: false,
              message: `The action '${updateAction.action}' is not valid.`,
              level: AlertLevel._0_CRITICAL,
            });
        }
        if (updateAction.key in currentStats.rankData) {
          return this.addItemAlert(updateAction.key, {
            success: true,
            message: `Rank for '${updateAction.key}' updated to ${
              currentStats.rankData[updateAction.key].rank
            }.`,
            level: AlertLevel._4_LOG,
          });
        }
        return this.addItemAlert(updateAction.key, {
          success: true,
          message: `Removed '${updateAction.key}'.`,
          level: AlertLevel._4_LOG,
        });
      }
    );
    this.revisionHistory = revisionHistory.concat([currentStats]);
    this.revisionNumber = revisionHistory.length;
    this.externalCallback();
    // TODO: return all results to give feedback to user
    return results[0];
  };
}
